import { useEffect, useState } from "react";
import { Phone, Menu, X, Home, Briefcase, Workflow, Building2, ShieldCheck, ArrowRight } from "lucide-react";
import { PHONE_DISPLAY, PHONE_LINK, LOGO_URL } from "@/lib/constants";

const desktopNav = [
  { label: "Services", href: "#service" },
  { label: "Process", href: "#process" },
  { label: "FAQ", href: "#faq" },
];

const mobileNav = [
  { label: "Accueil", href: "#top", icon: Home },
  { label: "Nos services", href: "#service", icon: Briefcase },
  { label: "Comment ça fonctionne", href: "#process", icon: Workflow },
  { label: "Entreprises concernées", href: "#pour-qui", icon: Building2 },
  { label: "Sécurité & confidentialité", href: "#securite", icon: ShieldCheck },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Prevent body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
            : "bg-white/80 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="header-logo-link" onClick={() => setOpen(false)}>
            <img
              src={LOGO_URL}
              alt="ArchivExpress — Destruction d'archives en PACA"
              className="h-11 md:h-12 w-auto"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {desktopNav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors"
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={`tel:${PHONE_LINK}`}
              className="hidden sm:inline-flex items-center gap-2 bg-slate-950 text-white font-bold py-2.5 px-4 md:py-3 md:px-5 rounded-full hover:bg-blue-600 transition-all text-sm"
              data-testid="header-call-button"
            >
              <Phone className="h-4 w-4" />
              <span>{PHONE_DISPLAY}</span>
            </a>
            <a
              href="#devis"
              className="hidden md:inline-flex items-center bg-blue-600 text-white font-bold py-3 px-5 rounded-full hover:bg-blue-700 transition-all text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5"
              data-testid="header-quote-button"
            >
              Devis gratuit
            </a>
            <button
              className="lg:hidden relative p-2 rounded-xl text-slate-900 hover:bg-slate-100 transition-colors"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
              data-testid="header-mobile-toggle"
            >
              <span className="relative block w-6 h-6">
                <Menu className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${open ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"}`} />
                <X className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${open ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-75"}`} />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu — premium fullscreen overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-30 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
        data-testid="mobile-menu"
      >
        {/* Backdrop */}
        <button
          className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          aria-label="Fermer le menu"
          tabIndex={-1}
        />

        {/* Panel */}
        <div
          className={`absolute top-16 left-0 right-0 bg-white rounded-b-3xl shadow-2xl border-b border-slate-200 transition-all duration-400 ease-out ${
            open ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
        >
          <nav className="px-5 pt-6 pb-3">
            {mobileNav.map((item, i) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="group flex items-center gap-4 py-4 border-b border-slate-100 last:border-b-0 transition-colors hover:bg-slate-50 -mx-3 px-3 rounded-xl"
                  style={{
                    animation: open ? `fadeInUp 0.5s ${0.05 + i * 0.05}s cubic-bezier(0.22, 1, 0.36, 1) both` : "none",
                  }}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="h-10 w-10 rounded-xl bg-slate-100 group-hover:bg-blue-600 text-slate-700 group-hover:text-white flex items-center justify-center transition-colors shrink-0">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-lg font-bold text-slate-950 tracking-tight flex-1">
                    {item.label}
                  </span>
                  <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all shrink-0" />
                </a>
              );
            })}
          </nav>

          {/* Primary CTA — Demander un devis */}
          <div className="px-5 pb-5 pt-2 space-y-3">
            <a
              href="#devis"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg shadow-blue-600/30 active:bg-blue-700 transition-colors text-base"
              data-testid="mobile-quote-button"
              style={{ animation: open ? "fadeInUp 0.5s 0.35s cubic-bezier(0.22, 1, 0.36, 1) both" : "none" }}
            >
              Demander un devis
              <ArrowRight className="h-5 w-5" />
            </a>

            {/* Phone block */}
            <a
              href={`tel:${PHONE_LINK}`}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between gap-3 w-full bg-slate-950 text-white py-4 px-5 rounded-2xl active:bg-slate-800 transition-colors"
              data-testid="mobile-call-button"
              style={{ animation: open ? "fadeInUp 0.5s 0.4s cubic-bezier(0.22, 1, 0.36, 1) both" : "none" }}
            >
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Phone className="h-5 w-5" />
                </span>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">Appeler</div>
                  <div className="font-display text-xl font-extrabold tracking-tight">{PHONE_DISPLAY}</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-white/60" />
            </a>

            <p className="text-[11px] text-center text-slate-500 font-medium pt-1">
              Réponse sous 24h · Confidentialité garantie
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
